const STORAGE_KEY = 'corpintel_ai_chat_sessions';
const MAX_SESSIONS = 30;
const MAX_TITLE_LENGTH = 48;

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function deriveChatTitle(question) {
  const clean = (question || '').replace(/\s+/g, ' ').trim();
  if (!clean) return 'New chat';
  return clean.length > MAX_TITLE_LENGTH ? `${clean.slice(0, MAX_TITLE_LENGTH - 1)}…` : clean;
}

function isPlaceholderTitle(title) {
  const t = (title || '').trim();
  return !t || t.toLowerCase() === 'new chat';
}

/** Use a saved title when present; otherwise name the chat from its first question. */
export function resolveChatTitle(session) {
  if (!isPlaceholderTitle(session?.title)) return session.title.trim();
  const firstTurn = session?.turns?.find(turn => (
    (turn?.displayQuestion || '').trim() || (turn?.question || '').trim()
  ));
  return deriveChatTitle(firstTurn?.displayQuestion || firstTurn?.question);
}

// The assistant opens every new chat by saying what it can do, one line each,
// so nobody has to guess what to type first.
const GREETING_TEXT = [
  "Here's what I can do:",
  '- Answer questions about any monitored company, always with sources',
  '- Pull the key risks, or a short brief you can send the team',
  '- Tell you what changed in the last 7 days',
  '- Start monitoring a new company — just say "add Tesla"',
].join('\n');

const GREETING_CHIPS = [
  { label: "This week's risks", question: "Summarize this week's high-risk alerts" },
  { label: 'Adani legal actions', question: 'What legal actions involve Adani Group?' },
  { label: 'Needs attention', question: 'Which companies need immediate attention?' },
];

function greetingTurn() {
  return {
    id: `${makeId()}-greeting`,
    greeting: true,
    question: null,
    answer: GREETING_TEXT,
    sources: [],
    followUps: GREETING_CHIPS,
    loading: false,
    error: null,
  };
}

export function createChatSession() {
  return { id: makeId(), title: 'New chat', createdAt: Date.now(), turns: [greetingTurn()] };
}

// The greeting doesn't count as content: without this, "New chat" would stack a
// fresh session every time instead of reusing the blank one, and the duplicate
// collapsing below would never fire.
function isBlankSession(session) {
  return !(session?.turns || []).some(turn => !turn?.greeting);
}

/**
 * Drop stuck loading flags and collapse duplicate empty "New chat" rows
 * so refresh / remount never floods the sidebar with blank sessions.
 */
export function normalizeChatSessions(sessions) {
  if (!Array.isArray(sessions)) return [];

  const cleaned = sessions
    .filter(s => s && typeof s === 'object' && s.id)
    .map(s => ({
      ...s,
      createdAt: s.createdAt || Date.now(),
      turns: (s.turns || []).map(turn => (
        turn?.loading
          ? {
              ...turn,
              loading: false,
              error: turn.error || (turn.answer ? null : 'Interrupted — try again'),
            }
          : turn
      )),
    }))
    .map(s => ({
      ...s,
      title: resolveChatTitle(s),
    }));

  // Chats saved before the greeting existed still open empty - give them one.
  const withGreeting = cleaned.map(s => (
    isBlankSession(s) && !s.turns.some(turn => turn?.greeting)
      ? { ...s, turns: [greetingTurn()] }
      : s
  ));

  const withContent = withGreeting.filter(s => !isBlankSession(s));
  const blanks = withGreeting.filter(isBlankSession);
  // Keep at most one empty session (prefer the most recent blank)
  const blank = blanks.length > 0
    ? [blanks.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0]]
    : [];

  return [...blank, ...withContent].slice(0, MAX_SESSIONS);
}

export function loadChatSessions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return normalizeChatSessions(Array.isArray(parsed) ? parsed : []);
  } catch (err) {
    return [];
  }
}

export function saveChatSessions(sessions) {
  try {
    const normalized = normalizeChatSessions(sessions);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized.slice(0, MAX_SESSIONS)));
  } catch (err) {
    // localStorage unavailable or full — persistence is a nice-to-have, safe to skip
  }
}

/** Prefer reusing an existing empty chat instead of spawning another "New chat". */
export function openOrCreateBlankSession(sessions, activeChatId) {
  const list = Array.isArray(sessions) ? sessions : [];
  const active = list.find(s => s.id === activeChatId);
  if (active && isBlankSession(active)) {
    return { sessions: list, activeChatId };
  }
  const existingBlank = list.find(isBlankSession);
  if (existingBlank) {
    return {
      sessions: [existingBlank, ...list.filter(s => s.id !== existingBlank.id)],
      activeChatId: existingBlank.id,
    };
  }
  const session = createChatSession();
  return {
    sessions: [session, ...list],
    activeChatId: session.id,
  };
}
